// src/pages/Cart.js
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiTrash2, FiShoppingCart } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { toast } from "react-toastify";

const getStableId = (item) => {
  if (item?.dealId && typeof item.dealId === "string") return item.dealId;
  if (item?.dealId && typeof item.dealId === "object") return item.dealId._id || item.dealId.id;
  return item?._id || item?.id || item?._localKey || null;
};

const Cart = () => {
  const { cartItems = [], updateQuantity, removeFromCart, clearCart, cartTotal = 0 } = useCart();
  const navigate = useNavigate();

  const items = Array.isArray(cartItems) ? cartItems : [];

  // ✅ PAS de Hook ici → pas d’erreur ESLint
  const totalItems = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <FiShoppingCart size={64} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Votre panier est vide</h2>
        <p className="text-gray-600 mb-8">
          Découvrez nos deals exceptionnels et profitez de réductions incroyables !
        </p>
        <Link
          to="/deals"
          className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700"
        >
          Explorer les deals
        </Link>
      </div>
    );
  }

  const subtotal =
    Number(cartTotal) ||
    items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

  const TAX_RATE = 0.2;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  const handleDecrease = (item) => {
    const id = getStableId(item);
    if (!id) return;

    const qty = Number(item.quantity) || 1;
    const newQty = qty - 1;

    if (newQty <= 0) {
      if (window.confirm("Voulez-vous supprimer cet article du panier ?")) {
        removeFromCart(id);
        toast.success("Article retiré du panier");
      }
    } else {
      updateQuantity(id, newQty);
    }
  };

  const handleIncrease = (item) => {
    const id = getStableId(item);
    if (!id) return;

    const qty = Number(item.quantity) || 1;
    updateQuantity(id, qty + 1);
  };

  const handleRemove = (item) => {
    const id = getStableId(item);
    if (!id) return;
    removeFromCart(id);
    toast.success("Article retiré du panier");
  };

  const handleClear = () => {
    if (window.confirm("Vider le panier ? Cette action est irréversible.")) {
      clearCart();
      toast.success("Panier vidé");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">
          Mon Panier{" "}
          <span className="text-lg text-gray-500 ml-2">
            ({totalItems} article{totalItems > 1 ? "s" : ""})
          </span>
        </h1>

        <button
          onClick={handleClear}
          className="text-red-600 hover:text-red-700 font-semibold"
          aria-label="Vider le panier"
        >
          Vider le panier
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Items */}
        <div className="md:col-span-2 space-y-4">
          {items.map((item, idx) => {
            const id = getStableId(item) || `${idx}`;
            const qty = Math.max(1, Number(item.quantity) || 1);
            const price = Number(item.price) || 0;

            return (
              <div key={id} className="bg-white rounded-lg shadow p-4 flex">
                <img
                  src={item.image || "/placeholder.png"}
                  alt={item.title || "Deal image"}
                  className="w-24 h-24 object-cover rounded"
                />

                <div className="flex-1 ml-4">
                  <h3 className="font-semibold text-lg mb-2">{item.title || "Produit"}</h3>

                  <p className="text-primary-600 font-bold text-xl mb-2">
                    {price.toFixed(2)} MAD
                  </p>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDecrease(item)}
                        className="w-8 h-8 border rounded hover:bg-gray-100 flex items-center justify-center"
                        aria-label={`Diminuer la quantité de ${item.title || "cet article"}`}
                        title={qty <= 1 ? "Supprimer l'article" : "Diminuer"}
                      >
                        -
                      </button>

                      <span className="w-8 text-center font-semibold">{qty}</span>

                      <button
                        onClick={() => handleIncrease(item)}
                        className="w-8 h-8 border rounded hover:bg-gray-100 flex items-center justify-center"
                        aria-label={`Augmenter la quantité de ${item.title || "cet article"}`}
                        title="Augmenter"
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemove(item)}
                      className="text-red-600 hover:text-red-700"
                      aria-label={`Retirer ${item.title || "cet article"} du panier`}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <p className="font-bold text-xl">{(price * qty).toFixed(2)} MAD</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Résumé */}
        <div>
          <div className="bg-white rounded-lg shadow p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-2">Résumé</h2>
            <p className="text-sm text-gray-500 mb-4">
              {totalItems} article{totalItems > 1 ? "s" : ""} dans votre panier
            </p>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-gray-600">
                <span>Sous-total</span>
                <span>{subtotal.toFixed(2)} MAD</span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span>TVA (20%)</span>
                <span>{tax.toFixed(2)} MAD</span>
              </div>

              <div className="border-t pt-3 flex justify-between font-bold text-xl">
                <span>Total</span>
                <span className="text-primary-600">{total.toFixed(2)} MAD</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 mb-3"
            >
              Passer la commande
            </button>

            <Link
              to="/deals"
              className="block text-center text-primary-600 hover:text-primary-700 font-semibold"
            >
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
